import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Loader from '../../../Loader'
import { generatePresignedUrl } from '../../../../helpers/s3'
import use3DViewerEvent from '../../../../hooks/use3DViewerEvent'
import use3DViewer from '../../../../hooks/use3DViewer'
import { ModelLoaderProgress } from '../../../ModelLoaderProgress'
import { AppConfigContext } from '../../../../context/AppConfig'
import { getHotjarSiteId } from '../../../../analytics/hotjar'
import './styles.css'
import VariantWrapper from '../../../VariantWrapper'
import { variants } from '../../../../constants/variant'
import { getDesignLayoutForUnitPlan } from '../../../../helpers/data'
import { useStore } from '../../../../store'
import ScreenType from '../../../ScreenType'

function usePrevious(value) {
  const ref = useRef()

  useEffect(() => {
    ref.current = value // Update the ref value after render
  }, [value])

  return ref.current // Return the previous value
}

function View({
  className = '',
  selectedLevel,
  selectedPackage,
  onModelLoadingChange,
  showCenterLoader,
  selectedLine = {},
}) {
  const { data } = useStore()

  const [loadedModelLine, setLoadedModelLine] = useState(false)
  const [modelLoading, setModelLoading] = useState(true)
  const previousLine = usePrevious(selectedLine)
  const [viewerFrameLoaded, setViewerFrameLoaded] = useState(false)
  const modelData = useMemo(() => {
    const plan = selectedPackage.unit_plans.find((p) => p.unit_type_level.id === selectedLevel.id)
    const { initial_gravity_rotation_angle, scale, high_res_model_path } =
      getDesignLayoutForUnitPlan(plan)?.models[0] || {}
    if (!high_res_model_path) {
      return {}
    }
    return {
      url: high_res_model_path,
      initial_gravity_rotation_angle,
      scale: scale || 1,
      transition: selectedLine.id !== previousLine?.id,
      isAnimateFromTopView: data?.property?.properties_configuration_options?.animate_unit_model_from_top_view ?? true, // setup default to true
    }
  }, [selectedLevel, selectedPackage, selectedLine, previousLine])

  const [loadingTimer, setLoadingTimer] = useState(null)
  const [showLoaderDelay, setShowLoaderDelay] = useState(false)

  const [initialLoad, setInitialLoad] = useState(true)

  const onModelLoaded = useCallback(() => {
    setLineChangedTransition(false)
    setModelLoading(false)
    if (loadingTimer) {
      clearTimeout(loadingTimer)
    }
    setShowLoaderDelay(false)
    if (selectedLine.id !== loadedModelLine) {
      setLoadedModelLine(selectedLine.id)

      document.dispatchEvent(new CustomEvent('proceed-animation'))
    }
    if (initialLoad) {
      setInitialLoad(false)
    }
  }, [initialLoad, loadedModelLine, selectedLine.id, loadingTimer])

  const onLoadingChange = useCallback(
    (loading) => {
      if (loading) {
        setModelLoading(true)
      }
      onModelLoadingChange(loading)
    },
    [onModelLoadingChange],
  )

  // useEffect(() => {
  //   let timeout

  //   if (!showCenterLoader && !initialLoad && modelLoading) {
  //     timeout = setTimeout(() => {
  //       setShowLoaderDelay(true)
  //     }, 1000)
  //     setLoadingTimer(timeout)
  //   }
  //   return ()=>{
  //     if(timeout){
  //       clearTimeout(timeout)
  //       setLoadingTimer(null)
  //     }
  //   }
  // }, [modelData, showCenterLoader, modelLoading, initialLoad])

  const [lineChangedTransition, setLineChangedTransition] = useState(true)

  useEffect(() => {
    setLineChangedTransition(true)
    send('select-line', selectedLine)
  }, [selectedLine])

  const iframeRef = useRef(null)

  const { send } = use3DViewer(iframeRef)

  useEffect(() => {
    ;(async () => {
      if (!modelData.url) {
        return
      }
      const signed = await generatePresignedUrl(`${modelData.url}.glb`)
      send('url-change', {
        url: signed,
        angle: modelData.initial_gravity_rotation_angle,
        scale: modelData.scale,
        transition: modelData.transition,
        isAnimateFromTopView: modelData.isAnimateFromTopView,
      })
    })()
  }, [modelData, send, viewerFrameLoaded])

  use3DViewerEvent('model-loaded', (data) => {
    onModelLoaded()
  })

  use3DViewerEvent('model-loading-change', ({ payload }) => {
    if (payload.loading) {
      setLoadingTimer(
        setTimeout(() => {
          setShowLoaderDelay(true)
        }, 1000),
      )
    } else {
      if (loadingTimer) {
        setShowLoaderDelay(false)
        clearTimeout(loadingTimer)
      }
    }
    onLoadingChange(payload.loading)
  })

  const { isBrodskyApp, isOfficesApp, searchParams, isSFApp } = useContext(AppConfigContext)

  const hotjarSideId = getHotjarSiteId()

  const params = searchParams
    ? Object.entries(searchParams).reduce((acc, [key, value]) => `${acc}&${key}=${value}`, '')
    : ''

  return (
    modelData && (
      <div
        className={`h-full w-full flex items-center justify-center overflow-hidden view ${
          isBrodskyApp ? 'brodsky' : ''
        } ${className}`}
      >
        {((showCenterLoader && initialLoad) || (!initialLoad && showLoaderDelay)) && (
          <div
            className={` absolute top-[50%] left-[50%] ml-[-75px] mt-[-75px] z-10 h-[150px] w-[150px] flex flex-col items-center justify-center`}
          >
            <div
              className={`${!lineChangedTransition ? 'bg-[rgba(0,0,0,.2)] backdrop-blur-lg shadow-lg' : ''} rounded-xl`}
            >
              <div className={`${isBrodskyApp || isSFApp ? 'lg:scale-[0.8]' : 'scale-[0.8]'}`}>
                <Loader className={`${isBrodskyApp || isSFApp ? 'h-[64px] lg:h-auto mb-[12px] lg:mb-0' : ''}`} />
              </div>
            </div>
            <VariantWrapper show={[variants.br, variants.sf]}>
              <div className="text-[12px] lg:hidden mb-[6px]">Loading Models</div>
            </VariantWrapper>
            <div
              className={`${
                isBrodskyApp || isSFApp ? 'text-[12px] lg:text-[14px]' : 'text-[14px]'
              } text-[#3D4256] w-[203px] text-center lg:w-max  ${
                lineChangedTransition ? 'opacity-80' : 'hidden'
              } font-medium`}
            >
              {' '}
              Your furnished 3D experience is just moments away!
            </div>
          </div>
        )}

        <iframe
          data-hj-allow-iframe=""
          ref={iframeRef}
          onLoad={() => {
            setViewerFrameLoaded(true)
          }}
          src={`${
            process.env.REACT_APP_3D_VIEWER_URL || 'https://3d-viewer-dev.sparx.ai'
          }?url=empty&hide_background=true&loader=false${
            hotjarSideId ? `&hotjar_site_id=${hotjarSideId}` : ''
          }${params}`}
          className="h-full w-full"
          alt="3d-viewer"
          id="3d-viewer"
          title="3d-viewer"
        />
        {modelLoading && (
          <>
            <ScreenType>
              <ModelLoaderProgress
                className={`absolute  lg:bottom-[30px] left-[24px] ${
                  isOfficesApp ? 'bottom-[96px]' : 'bottom-[66px]'
                } ${isBrodskyApp || isSFApp ? 'hidden lg:block' : ''} lg:left-auto`}
              />
            </ScreenType>
            <ScreenType type="tv">
              <ModelLoaderProgress
                className={`absolute  lg:bottom-[130px] left-[124px] ${
                  isOfficesApp ? 'bottom-[196px]' : 'bottom-[166px]'
                } ${isBrodskyApp || isSFApp ? 'hidden lg:block' : ''} lg:left-auto`}
              />
            </ScreenType>
          </>
        )}
      </div>
    )
  )
}

export default View
